/**
 * Payment Service
 *
 * API calls for payment concepts domain
 */

import { apiClient } from '@/api/core/apiClient';
import { API_CONFIG } from '@/api/core/apiConfig';
import type { ApiResponse } from '@/api/shared/types';
import type { HybridExportResult } from '@/types/fileExport.types';
import type {
  LC_ExportPaymentConceptsIn,
  LC_GetPaymentConceptsIn,
  LC_GetPaymentConceptsListOut,
  PaymentConceptsListResult,
} from './payment.types';
import { mapPaymentConceptsListResponse } from './payment.mapper';


export class PaymentService {
  /**
   * Get paginated list of payment concepts generated in a monthly cut, with the advanced
   * dimensional filters panel (ASIS-5317). POST because the dimensional CSV filters travel
   * in the request body — precedent: POST /attendance/marks/search.
   */
  async listPaymentConcepts(
    input: LC_GetPaymentConceptsIn,
  ): Promise<ApiResponse<PaymentConceptsListResult>> {
    const endpoint = API_CONFIG.ENDPOINTS.PAYMENT.PAYMENT_CONCEPTS_SEARCH;

    const response = await apiClient.post<LC_GetPaymentConceptsListOut>(endpoint, input);

    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }

    return {
      success: true,
      data: mapPaymentConceptsListResponse(response.data),
      status: response.status,
    };
  }

  /**
   * Exports the payment concepts of a monthly cut to Excel (ASIS-5320), with the same
   * dimensional filters as the list search minus pagination/sort. Hybrid endpoint: 200 with the
   * file ready to download, or 202 with a background job id — see `downloadBlobOrAcceptJob`.
   */
  async exportPaymentConcepts(
    input: LC_ExportPaymentConceptsIn,
  ): Promise<ApiResponse<HybridExportResult>> {
    return apiClient.downloadBlobOrAcceptJob(
      API_CONFIG.ENDPOINTS.PAYMENT.PAYMENT_CONCEPTS_EXPORT,
      { method: 'POST', body: JSON.stringify(input) },
    );
  }
}

export const paymentService = new PaymentService();
